import { createContext, useState, useMemo } from "react";
import { createTheme } from "@mui/material/styles";

/** App-wide MUI `palette.secondary` when tenant has no `secondaryColor` in session / Redis */
export const SECONDARY_COLOR = "#F5272E";

function normalizeBrandingColor(value, fallback) {
  if (value == null) return fallback;
  const s = String(value).trim();
  return s !== "" ? s : fallback;
}

// color design tokens export
export const tokens = (mode) => ({
  ...(mode === "dark"
    ? {
        grey: {
          100: "#e0e0e0",
          200: "#c2c2c2",
          300: "#a3a3a3",
          400: "#858585",
          500: "#666666",
          600: "#525252",
          700: "#3d3d3d",
          800: "#292929",
          900: "#141414",
          1000: "#f20f0f",
        },
        primary: {
          100: "#d0d1d5",
          200: "#a1a4ab",
          300: "#727681",
          400: "#1F2A40",
          500: "#141b2d",
          600: "#101624",
          700: "#0c101b",
          800: "#080b12",
          900: "#040509",
        },
        greenAccent: {
          100: "#dbf5ee",
          200: "#b7ebde",
          300: "#94e2cd",
          400: "#70d8bd",
          500: "#4cceac",
          600: "#3da58a",
          700: "#2e7c67",
          800: "#1e5245",
          900: "#0f2922",
        },
        redAccent: {
          100: "#f8dcdb",
          200: "#f1b9b7",
          300: "#e99592",
          400: "#e2726e",
          500: "#db4f4a",
          600: "#af3f3b",
          700: "#832f2c",
          800: "#58201e",
          900: "#2c100f",
        },
        blueAccent: {
          100: "#e1e2fe",
          200: "#c3c6fd",
          300: "#a4a9fc",
          400: "#868dfb",
          500: "#6870fa",
          600: "#535ac8",
          700: "#3e4396",
          800: "#2a2d64",
          900: "#151632",
        },
      }
    : {
        grey: {
          100: "#000000",
          200: "#080000",
          300: "#3d3d3d",
          400: "#525252",
          500: "#666666",
          600: "#858585",
          700: "#a3a3a3",
          800: "#db0d0d",
          900: "#e0e0e0",
        },
        primary: {
          100: "#040509",
          200: "#8a0307",
          300: "#0c101b",
          400: "#ede2e2",
          500: "#141b2d",
          600: "#1F2A40",
          700: "#727681",
          800: "#a1a4ab",
          900: "#d0d1d5",
        },
        greenAccent: {
          100: "#0f2922",
          200: "#1e5245",
          300: "#2e7c67",
          400: "#3da58a",
          500: "#2416e6ff",
          600: "#120001",
          700: "#94e2cd",
          800: "#b7ebde",
          900: "#dbf5ee",
        },
        redAccent: {
          100: "#2c100f",
          200: "#58201e",
          300: "#832f2c",
          400: "#af3f3b",
          500: "#db4f4a",
          600: "#e2726e",
          700: "#e99592",
          800: "#f1b9b7",
          900: "#f8dcdb",
        },
        blueAccent: {
          100: "#151632",
          200: "#2a2d64",
          300: "#3e4396",
          400: "#535ac8",
          500: "#6870fa",
          600: "#868dfb",
          700: "#a4a9fc",
          800: "#c3c6fd",
          900: "#e1e2fe",
        },
      }),
});

/**
 * MUI theme settings.
 * @param {"light"|"dark"} mode
 * @param {object|null|undefined} tenantBranding — from GET /auth/context: primaryColor, secondaryColor only (background is not driven by tenant)
 */
export const themeSettings = (mode, tenantBranding = null) => {
  const colors = tokens(mode);
  const defaultPrimaryMain = mode === "dark" ? colors.primary[500] : colors.primary[100];
  const primaryMain = normalizeBrandingColor(tenantBranding?.primaryColor, defaultPrimaryMain);
  const secondaryMain = normalizeBrandingColor(tenantBranding?.secondaryColor, SECONDARY_COLOR);
  const backgroundDefault = mode === "dark" ? colors.primary[500] : "#FFFFFF";
  /** Same surface as the app sidebar in dark mode (`primary[400]`). */
  const backgroundPaper = mode === "dark" ? colors.primary[400] : "#FFFFFF";

  return {
    palette: {
      mode: mode,
      ...(mode === "dark"
        ? {
            primary: {
              main: primaryMain,
            },
            secondary: {
              main: secondaryMain,
              light: "#ff5c62",
              dark: "#c01d24",
              contrastText: "#ffffff",
            },
            neutral: {
              dark: colors.grey[700],
              main: colors.grey[500],
              light: colors.grey[100],
            },
            background: {
              default: backgroundDefault,
              paper: backgroundPaper,
            },
          }
        : {
            primary: {
              main: primaryMain,
            },
            secondary: {
              main: secondaryMain,
              light: "#ff5c62",
              dark: "#c01d24",
              contrastText: "#ffffff",
            },
            neutral: {
              dark: colors.grey[700],
              main: colors.grey[1000],
              light: colors.grey[100],
            },
            background: {
              default: backgroundDefault,
              paper: backgroundPaper,
            },
          }),
    },
    typography: {
      fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
      fontSize: 12,
      h1: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 40,
      },
      h2: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 32,
      },
      h3: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 24,
      },
      h4: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 20,
      },
      h5: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 16,
      },
      h6: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 14,
        color: mode === "light" ? "#000000" : colors.grey[100],
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: "none",
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: backgroundDefault,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: ({ theme: t }) =>
            t.palette.mode === "dark"
              ? {
                  backgroundImage: "none",
                  backgroundColor: t.palette.background.paper,
                }
              : {},
        },
      },
      MuiCard: {
        styleOverrides: {
          root: ({ theme: t }) =>
            t.palette.mode === "dark"
              ? {
                  backgroundImage: "none",
                  backgroundColor: t.palette.background.paper,
                }
              : {},
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: ({ theme: t }) =>
            t.palette.mode === "dark"
              ? {
                  backgroundImage: "none",
                  backgroundColor: t.palette.background.paper,
                }
              : {},
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: ({ theme: t }) =>
            t.palette.mode === "dark"
              ? {
                  backgroundColor: t.palette.background.paper,
                }
              : {},
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: ({ theme: t }) => ({
            textAlign: "center",
            textTransform: "none",
            fontWeight: 700,
            fontSize: "1.35rem",
            letterSpacing: "normal",
            ...(t.palette.mode === "dark"
              ? { backgroundColor: t.palette.background.paper }
              : {}),
          }),
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: ({ theme: t }) =>
            t.palette.mode === "dark"
              ? {
                  backgroundColor: t.palette.background.paper,
                }
              : {},
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: ({ theme: t }) =>
            t.palette.mode === "dark"
              ? {
                  backgroundImage: "none",
                  backgroundColor: t.palette.background.paper,
                }
              : {},
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: ({ theme: t }) =>
            t.palette.mode === "dark"
              ? {
                  backgroundImage: "none",
                  backgroundColor: t.palette.background.paper,
                }
              : {},
        },
      },
      MuiDataGrid: {
        styleOverrides: {
          root: ({ theme: t }) =>
            t.palette.mode === "dark"
              ? {
                  backgroundColor: t.palette.background.paper,
                  borderColor: t.palette.divider,
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: t.palette.background.paper,
                  },
                  "& .MuiDataGrid-filler": {
                    backgroundColor: t.palette.background.paper,
                  },
                  "& .MuiDataGrid-footerContainer": {
                    backgroundColor: t.palette.background.paper,
                  },
                  "& .MuiDataGrid-virtualScroller": {
                    backgroundColor: t.palette.background.paper,
                  },
                }
              : {},
        },
      },
    },
  };
};

// context for color mode
export const ColorModeContext = createContext({
  toggleColorMode: () => {},
});

/**
 * Theme + light/dark toggle. Pass `tenantBranding` from session (`user.data.tenantBranding`) so
 * primary/secondary match values cached in Redis after login.
 */
export const useMode = (tenantBranding = null) => {
  const [mode, setMode] = useState("light"); // ✅ default is now light mode

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () =>
        setMode((prev) => (prev === "light" ? "dark" : "light")),
    }),
    []
  );

  const theme = useMemo(
    () => createTheme(themeSettings(mode, tenantBranding)),
    [mode, tenantBranding?.primaryColor, tenantBranding?.secondaryColor]
  );
  return [theme, colorMode];
};
